/**
 * Class Progress Tab — התקדמות כיתות
 *
 * Per-class report of student progress through the learning path, plus a CSV
 * export. The rows contain student PII, so the backend restricts this to admins
 * and audits every read and export with a PII_ACCESS compliance flag.
 */

import { useEffect, useState } from 'react'
import { AlertCircle, Download, RefreshCw } from 'lucide-react'
import { useAdminStore } from '../../store/adminStore'

export default function ClassProgressTab() {
  const {
    classCodes,
    fetchClassCodes,
    classProgress,
    classProgressSummary,
    classProgressLoading,
    classProgressError,
    fetchClassProgress,
    exportClassProgress,
  } = useAdminStore()

  const [classCodeId, setClassCodeId] = useState('')
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    void fetchClassCodes()
  }, [fetchClassCodes])

  useEffect(() => {
    void fetchClassProgress(classCodeId || undefined)
  }, [classCodeId, fetchClassProgress])

  const handleExport = async () => {
    setExporting(true)
    try {
      await exportClassProgress(classCodeId || undefined)
    } catch {
      /* store surfaces the error */
    } finally {
      setExporting(false)
    }
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900">התקדמות כיתות</h2>
        <p className="mt-1 text-sm text-gray-600">
          מעקב אחרי התקדמות התלמידים במסלול הלמידה וציוני המבחן המסכם.
        </p>
      </div>

      {classProgressError && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700" role="alert">
          <AlertCircle size={16} aria-hidden="true" /> {classProgressError}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="class-filter" className="mb-1 block text-sm font-medium text-gray-700">
            כיתה
          </label>
          <select
            id="class-filter"
            value={classCodeId}
            onChange={(e) => setClassCodeId(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">כל הכיתות</option>
            {classCodes.map((c) => (
              <option key={c._id} value={c._id}>
                {c.code} — {c.label}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || classProgress.length === 0}
          className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {exporting ? <RefreshCw className="animate-spin" size={16} /> : <Download size={16} />}
          ייצוא ל-CSV
        </button>
      </div>

      {classProgressSummary && classProgress.length > 0 && (
        <dl className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <dt className="text-xs text-gray-600">תלמידים</dt>
            <dd className="text-xl font-bold text-gray-900">{classProgressSummary.studentCount}</dd>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <dt className="text-xs text-gray-600">עברו את המבחן</dt>
            <dd className="text-xl font-bold text-gray-900">{classProgressSummary.passedCount}</dd>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <dt className="text-xs text-gray-600">ציון ממוצע</dt>
            <dd className="text-xl font-bold text-gray-900" dir="ltr">
              {classProgressSummary.averageBestScore}%
            </dd>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <dt className="text-xs text-gray-600">נושאים במסלול</dt>
            <dd className="text-xl font-bold text-gray-900">{classProgressSummary.totalLessons}</dd>
          </div>
        </dl>
      )}

      {classProgressLoading && classProgress.length === 0 && (
        <p className="py-8 text-center text-sm text-gray-600">טוען...</p>
      )}

      {!classProgressLoading && classProgress.length === 0 && (
        <p className="rounded-lg bg-gray-50 p-6 text-center text-sm text-gray-700">
          אין עדיין נתוני התקדמות לכיתה זו.
        </p>
      )}

      {classProgress.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <caption className="sr-only">התקדמות תלמידים במסלול הלמידה</caption>
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th scope="col" className="px-3 py-2 text-right font-semibold text-gray-900">תלמיד/ה</th>
                <th scope="col" className="px-3 py-2 text-right font-semibold text-gray-900">כיתה</th>
                <th scope="col" className="px-3 py-2 text-right font-semibold text-gray-900">נושאים</th>
                <th scope="col" className="px-3 py-2 text-right font-semibold text-gray-900">שאלות למדריך</th>
                <th scope="col" className="px-3 py-2 text-right font-semibold text-gray-900">ניסיונות</th>
                <th scope="col" className="px-3 py-2 text-right font-semibold text-gray-900">ציון</th>
                <th scope="col" className="px-3 py-2 text-right font-semibold text-gray-900">פעילות אחרונה</th>
              </tr>
            </thead>
            <tbody>
              {classProgress.map((row) => (
                <tr key={row.userId} className="border-b border-gray-100">
                  <td className="px-3 py-2 text-gray-900">
                    {row.firstName} {row.lastName}
                    <span className="block text-xs text-gray-600" dir="ltr">{row.email}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-800">{row.classCode || '—'}</td>
                  <td className="px-3 py-2 text-gray-800">
                    {row.completedCount}/{row.totalLessons}
                    <span className="ms-1 text-xs text-gray-600" dir="ltr">({row.percent}%)</span>
                  </td>
                  <td className="px-3 py-2 text-gray-800">{row.chatTurnsTotal}</td>
                  <td className="px-3 py-2 text-gray-800">{row.attemptsCount}</td>
                  <td className="px-3 py-2">
                    <span className="text-gray-900" dir="ltr">{row.bestScorePercent}%</span>
                    <span className={`ms-2 text-xs ${row.passed ? 'text-green-800' : 'text-gray-600'}`}>
                      {row.passed ? 'עבר/ה' : 'טרם'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {new Date(row.lastActivityAt).toLocaleDateString('he-IL')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
